import { useApi } from '@/providers/apiProvider';
import { Cart } from '@/types/cart';
import {
  CartErrors,
  CartHealthSummary,
  ThisWeek,
  CartStats,
  CartStatsCartsPage,
  BatteryCompliance,
  BatteryComplianceDays,
  Customer,
  CustomerDashboard,
  FirmwareImpact,
  FleetOverview,
  SeatAndMotorUsageHistory,
  UsageHistoryFilterType,
  ChargingUse,
  ReadyAtOpening,
  ReadyAtOpeningDays,
  UsageSummary,
  UsageSummaryDays,
} from '@/types/customer';
import { useQuery, useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';

export const useGetCustomers = () => {
  const httpClient = useApi();

  const getCustomers = async () => {
    const response = await httpClient.get<Customer[]>(`/customers/`);
    return response.data;
  };

  return useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers,
  });
};

export const useGetCustomerById = (customerId: string) => {
  const httpClient = useApi();

  const getCustomerById = async (customerId: string) => {
    const response = await httpClient.get<Customer>(
      `/customers/${customerId}/`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => getCustomerById(customerId),
    enabled: !!customerId,
  });
};

export const useGetCustomerCarts = (customerId: string) => {
  const httpClient = useApi();

  const getListing = async () => {
    const response = await httpClient.get<Cart[]>(
      `/customers/${customerId}/carts/`
    );
    return response.data;
  };

  return useQuery({
    enabled: !!customerId,
    queryKey: ['customerCarts', customerId],
    queryFn: getListing,
  });
};

/**
 * The carts behind one Cart Status row or tile, 50 at a time, fetched only
 * while that row is open. `query` is the row's filter, e.g. "filter=stale".
 */
export const useGetCartStatsCarts = (customerId: string, query: string | null) => {
  const httpClient = useApi();

  return useInfiniteQuery({
    queryKey: ['cartStatsCarts', customerId, query],
    queryFn: async ({ pageParam }) => {
      const response = await httpClient.get<CartStatsCartsPage>(
        `/customers/${customerId}/carts/stats/carts/?${query}&limit=50&offset=${pageParam}`
      );
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
    enabled: !!customerId && !!query,
  });
};

export const useGetCartsStateStats = (customerId: string) => {
  const httpClient = useApi();

  const getCartsStateStats = async () => {
    const response = await httpClient.get<CartStats>(
      `/customers/${customerId}/carts/stats/`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['cartsStateStats', customerId],
    queryFn: getCartsStateStats,
    enabled: !!customerId,
  });
};

export const useGetBatteryCompliance = (
  customerId: string,
  days: BatteryComplianceDays
) => {
  const httpClient = useApi();

  const getBatteryCompliance = async () => {
    const response = await httpClient.get<BatteryCompliance>(
      `/customers/${customerId}/carts/battery-compliance/?days=${days}`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['batteryCompliance', customerId, days],
    queryFn: getBatteryCompliance,
    enabled: !!customerId,
  });
};

export const useGetSeatAndMotorUsageHistory = (
  customerId: string,
  range: UsageHistoryFilterType,
  startDate: string,
  endDate: string
) => {
  const httpClient = useApi();

  const getSeatAndMotorUsageHistory = async () => {
    const response = await httpClient.get<SeatAndMotorUsageHistory>(
      `/customers/${customerId}/carts/usage-history/${range}/?start_date=${startDate}&end_date=${endDate}`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['seatAndMotorUsageHistory', customerId, range],
    queryFn: getSeatAndMotorUsageHistory,
    enabled: !!customerId,
  });
};

export const useGetCartErrors = (
  customerId: string,
  range: UsageHistoryFilterType,
  startDate: string,
  endDate: string
) => {
  const httpClient = useApi();

  const getCartErrors = async () => {
    const response = await httpClient.get<CartErrors>(
      `/customers/${customerId}/carts/errors/${range}/?start_date=${startDate}&end_date=${endDate}`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['cartErrors', customerId, range],
    queryFn: getCartErrors,
    enabled: !!customerId,
  });
};

export const useGetCartHealthSummary = (customerId: string) => {
  const httpClient = useApi();

  const getCartHealthSummary = async () => {
    const response = await httpClient.get<CartHealthSummary>(
      `/customers/${customerId}/cart-health/summary/`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['cartHealthSummary', customerId],
    queryFn: getCartHealthSummary,
    enabled: !!customerId,
  });
};

export const useGetFirmwareImpact = (customerId: string) => {
  const httpClient = useApi();

  const getFirmwareImpact = async () => {
    const response = await httpClient.get<FirmwareImpact>(
      `/customers/${customerId}/firmware-impact/`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['firmwareImpact', customerId],
    queryFn: getFirmwareImpact,
    enabled: !!customerId,
  });
};

export const useGetThisWeek = (customerId: string) => {
  const httpClient = useApi();

  const getThisWeek = async () => {
    const response = await httpClient.get<ThisWeek>(`/customers/${customerId}/this-week/`);
    return response.data;
  };

  return useQuery({
    queryKey: ['thisWeek', customerId],
    queryFn: getThisWeek,
    enabled: !!customerId,
  });
};

export const useGetUsageSummary = (customerId: string, days: UsageSummaryDays) => {
  const httpClient = useApi();

  const getUsageSummary = async () => {
    const response = await httpClient.get<UsageSummary>(
      `/customers/${customerId}/carts/usage-summary/?days=${days}`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['usageSummary', customerId, days],
    queryFn: getUsageSummary,
    enabled: !!customerId,
  });
};

export const useGetReadyAtOpening = (customerId: string, days: ReadyAtOpeningDays) => {
  const httpClient = useApi();

  const getReadyAtOpening = async () => {
    const response = await httpClient.get<ReadyAtOpening>(
      `/customers/${customerId}/ready-at-opening/?days=${days}`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['readyAtOpening', customerId, days],
    queryFn: getReadyAtOpening,
    enabled: !!customerId,
    placeholderData: keepPreviousData,
  });
};

export const useGetChargingUse = (customerId: string) => {
  const httpClient = useApi();

  const getChargingUse = async () => {
    const response = await httpClient.get<ChargingUse>(
      `/customers/${customerId}/charging-use/`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['chargingUse', customerId],
    queryFn: getChargingUse,
    enabled: !!customerId,
  });
};

export const useGetFleetOverview = (customerId: string, locationId?: string) => {
  const httpClient = useApi();

  const getFleetOverview = async () => {
    const params = locationId ? `?location_id=${locationId}` : '';
    const response = await httpClient.get<FleetOverview>(
      `/customers/${customerId}/fleet-overview/${params}`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['fleetOverview', customerId, locationId],
    queryFn: getFleetOverview,
    enabled: !!customerId,
  });
};

export const useGetCustomerDashboard = (customerId: string, locationId?: string) => {
  const httpClient = useApi();

  const getCustomerDashboard = async () => {
    const params = locationId ? `?location_id=${locationId}` : '';
    const response = await httpClient.get<CustomerDashboard>(
      `/customers/${customerId}/dashboard/${params}`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['customerDashboard', customerId, locationId],
    queryFn: getCustomerDashboard,
    enabled: !!customerId,
    // Keep showing the previous store's data while a new store loads so the
    // shell (and store selector) stays mounted instead of flashing the
    // full-screen loader on every switch. Same customer only: another
    // customer's dashboard must never stand in for this one's.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === customerId
        ? keepPreviousData(previousData)
        : undefined,
  });
};

export type CartDetailStats = {
  modem_id: string;
  // Drive time (api#234), optional until the prod API serves it.
  drive_minutes_this_week?: number;
  avg_drive_minutes_per_day?: number; // last 30 days
  fleet_avg_drive_minutes_per_day?: number;
  last_activity: string | null;
  errors_last_30_days: number;
};

export const useGetCartDetail = (customerId: string, modemId: string) => {
  const httpClient = useApi();

  const getCartDetail = async () => {
    const response = await httpClient.get<CartDetailStats>(
      `/customers/${customerId}/carts/${modemId}/detail/`
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['cartDetail', customerId, modemId],
    queryFn: getCartDetail,
    enabled: !!customerId && !!modemId,
  });
};
